import React, { useState, useEffect } from 'react';
import { useSonas } from '../SonasContext';
import { Settings, LogOut, ShieldCheck, Copy, Menu, MessageCircle, Share2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from '@/integrations/supabase/AuthProvider';
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import PossessionsSheet from './PossessionsSheet';
import NotificationBell from './NotificationBell';

const MASTER_ADMIN_EMAIL = "arsgrouproblox@gmail.com";

const WorkspaceNavbar = ({ onToggleMobileSidebar }) => {
  const navigate = useNavigate();
  const { 
    isProjectConnected,
    setIsInstallModalOpen, 
    setIsStoreOpen, 
    setIsSettingsOpen,
    isPossessionsOpen,
    setIsPossessionsOpen,
    setIsAffiliateOpen,
    activeConversationId,
    conversations = {},
    setConversations
  } = useSonas();
  
  const { profile, user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState("");

  const currentProject = conversations?.[activeConversationId];
  const currentProjectName = currentProject?.name ?? "Untitled Project";
  const isAdmin = user?.email === MASTER_ADMIN_EMAIL || profile?.is_admin === true;

  useEffect(() => {
    setLocalName(currentProjectName);
  }, [currentProjectName, activeConversationId]);

  const handleSaveName = async () => {
    if (!activeConversationId || localName === currentProjectName) {
      setIsEditing(false);
      return;
    }

    try {
      await supabase.from('projects').update({ title: (localName ?? "").trim() || "Untitled Project" }).eq('id', activeConversationId);
      setConversations(prev => ({ ...prev, [activeConversationId]: { ...prev?.[activeConversationId], name: localName } }));
      setIsEditing(false);
      toast.success("Project renamed.");
    } catch (err) {
      toast.error("Rename failed.");
      setLocalName(currentProjectName);
    }
  };

  const handleCopyId = () => {
    if (!activeConversationId) return;
    const idToCopy = currentProject?.share_id || activeConversationId;
    navigator.clipboard.writeText(idToCopy);
    toast.success("Project ID copied");
  };

  return (
    <>
      <nav className="h-[52px] bg-sonas-bg border-b border-sonas-border px-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2 md:gap-4">
          <button className="p-1 rounded-md hover:bg-sonas-surface2 md:hidden" onClick={onToggleMobileSidebar}>
            <Menu className="h-5 w-5 text-sonas-dim" />
          </button>
          <div className="flex items-center gap-2">
            <img src={logo} alt="Sonas" className="h-5 w-auto cursor-pointer" onClick={() => navigate('/')} />
          </div>
          <div className="hidden md:block h-4 w-[1px] bg-sonas-border mx-1" />

          {isEditing ? (
            <input 
              autoFocus 
              className="bg-sonas-surface2 border border-sonas-border rounded px-2 py-0.5 text-xs font-medium w-32 md:w-48 outline-none"
              value={localName} 
              onChange={(e) => setLocalName(e.target.value)} 
              onBlur={handleSaveName} 
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
            />
          ) : (
            <div className="hidden sm:flex items-center gap-2 group">
              <span className="text-xs font-medium text-sonas-muted cursor-pointer hover:text-sonas-text transition-colors truncate max-w-[200px]" onClick={() => activeConversationId && setIsEditing(true)}>
                {localName || "Select Project"}
              </span>
              {activeConversationId && (
                <button onClick={handleCopyId} className="p-1 rounded-md text-sonas-dim hover:bg-sonas-surface2 opacity-0 group-hover:opacity-100">
                  <Copy size={12} />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <NotificationBell />

          <button 
            onClick={() => !isProjectConnected && setIsInstallModalOpen(true)} 
            className={cn("flex items-center gap-2 px-2 md:px-3 h-8 rounded-full border text-[9px] md:text-[10px] font-bold transition-all uppercase", 
              isProjectConnected ? "bg-sonas-accent/10 border-sonas-accent/20 text-sonas-accent" : "bg-sonas-surface2 border-sonas-border text-sonas-muted")}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", isProjectConnected ? "bg-sonas-accent" : "bg-sonas-dim")} />
            <span className="hidden xs:inline">{isProjectConnected ? "Linked" : "Not Connected"}</span>
          </button>

          <Button onClick={() => setIsStoreOpen(true)} className="h-8 px-2 md:px-3 bg-sonas-surface2 hover:bg-sonas-surface3 border border-sonas-border text-sonas-text font-bold text-[9px] md:text-[10px] rounded-full uppercase">
            Store · ${(profile?.balance || 0).toFixed(2)}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-8 w-8 rounded-full border border-sonas-border overflow-hidden bg-sonas-surface2 flex items-center justify-center">
                <Avatar className="h-full w-full">
                  <AvatarImage src={profile?.avatar_url} />
                  <AvatarFallback className="text-[10px] font-bold">
                    {profile?.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-sonas-surface border-sonas-border w-56 mt-2 shadow-xl" align="end">
              <DropdownMenuItem className="gap-2 cursor-pointer focus:bg-sonas-surface2" onClick={() => setIsSettingsOpen(true)}>
                <Settings className="h-3.5 w-3.5" /> Workspace Settings
              </DropdownMenuItem>

              <DropdownMenuItem className="gap-2 cursor-pointer focus:bg-blue-500/10" onClick={() => setIsAffiliateOpen(true)}>
                <Share2 className="h-3.5 w-3.5 text-blue-400" /> Affiliate Portal
              </DropdownMenuItem>

              <DropdownMenuItem className="gap-2 cursor-pointer focus:bg-indigo-500/10" onClick={() => window.open('https://discord.gg/S25DbuEVSy', '_blank')}>
                <MessageCircle className="h-3.5 w-3.5 text-indigo-400" /> Join Discord Server
              </DropdownMenuItem>
              
              {isAdmin && (
                <DropdownMenuItem className="gap-2 cursor-pointer text-sonas-accent focus:bg-sonas-accent/5 font-bold" onClick={() => navigate('/admin')}>
                  <ShieldCheck className="h-3.5 w-3.5" /> Admin Console
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator className="bg-sonas-border" />
              <DropdownMenuItem className="gap-2 cursor-pointer text-red-500 focus:bg-red-500/10" onClick={() => supabase.auth.signOut()}>
                <LogOut className="h-3.5 w-3.5" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <PossessionsSheet open={isPossessionsOpen} onOpenChange={setIsPossessionsOpen} />
    </>
  );
};

export default WorkspaceNavbar;